import { Box, Divider, Paper, Tooltip, Typography, alpha, type Theme } from "@mui/material";
import {
  CheckCircle as PassIcon,
  ErrorOutline as BlockingIcon,
  InfoOutlined as NoteIcon,
  RadioButtonUnchecked as PendingIcon,
  WarningAmber as AdvisoryIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import MUIButton from "@/components/ui/Button";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import type { ApproveStatus } from "../../utils/approveStatus";
import type { ReviewCheckCard, ReviewChecks, ReviewPassLine } from "../../utils/reviewChecks";

/** DOM id of the rail's status line; the Approve button's aria-describedby points here (#7432 AC1). */
export const RAIL_STATUS_ID = "review-rail-approve-status";

/**
 * Check tones from the theme palette, mode-aware: warning for advisory, error
 * for blocking. Text uses the dark shade on a light background and the light
 * shade on a dark one; the border and fill are alpha tints of the main colour,
 * so the card reads the same in both modes and under any partner brand. Brand
 * primary is reserved for the acknowledgement and Approve — the one action we
 * want to invite — while every other control on a card is neutral.
 */
type Tone = "advisory" | "blocking";
const toneText = (tone: Tone) => (t: Theme) => {
  const c = tone === "blocking" ? t.palette.error : t.palette.warning;
  return t.palette.mode === "dark" ? c.light : c.dark;
};
const toneBorder = (tone: Tone) => (t: Theme) =>
  alpha(tone === "blocking" ? t.palette.error.main : t.palette.warning.main, 0.5);
const toneFill = (tone: Tone) => (t: Theme) =>
  alpha(tone === "blocking" ? t.palette.error.main : t.palette.warning.main, t.palette.mode === "dark" ? 0.16 : 0.1);

/** Neutral outlined control: grey border, dark text, regardless of brand primary. */
const neutralButtonSx = {
  color: "text.primary",
  borderColor: "divider",
  "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
} as const;

interface BeforeYouApproveRailProps {
  status: ApproveStatus;
  checks: ReviewChecks;
  isApproving: boolean;
  /** Opens the approve confirm (#7440) — always, never a direct approve. */
  onApprove: () => void;
  /** False for a viewer: no acknowledge / exclude / fix controls. */
  canFix: boolean;
  onAcknowledge: (check: string, detail: string) => void;
  /** "Fix the extraction" controls; absent → hidden. */
  onReviewMapping?: () => void;
  onReprocess?: () => void;
  isReprocessing?: boolean;
  /** Why Field mapping / Reprocess are disabled, when they are. */
  fixDisabledReason?: string;
  /** Scope note on a paged document (AC5 of #7440 / design): what the checks cover. */
  scopeNote?: string | null;
}

/**
 * The "Before you approve" rail (#7432, artboards 1a/1c/2b).
 *
 * Pinned header: a full-width Approve and load, ONE status line (the button's
 * aria-describedby), then the heading. Below a divider the cards needing
 * attention (blocking in error tone first, then advisory), then the pass
 * lines, then "Fix the extraction". The list scrolls inside the rail so the
 * header never leaves view (decided 2026-10-13: the action sits above the
 * heading, so the rail reads "before you approve — approve").
 *
 * Replaces three surfaces: the errored-records banner above the tabs, the
 * Mapping Review sidebar card, and the projection advisories card.
 */
export const BeforeYouApproveRail = ({
  status,
  checks,
  isApproving,
  onApprove,
  canFix,
  onAcknowledge,
  onReviewMapping,
  onReprocess,
  isReprocessing = false,
  fixDisabledReason,
  scopeNote,
}: BeforeYouApproveRailProps) => {
  const disabled = status.blocked || isApproving;
  const tone =
    status.kind === "ready"
      ? "success.main"
      : status.kind === "attention" || status.kind === "no-client"
        ? toneText("advisory")
        : "text.secondary";
  const StatusIcon =
    status.kind === "ready"
      ? PassIcon
      : status.kind === "checking"
        ? PendingIcon
        : status.kind === "attention"
          ? AdvisoryIcon
          : null;
  const statusText = status.kind === "ready" ? "All checks pass" : status.why;
  const showFix = canFix && (onReviewMapping || onReprocess);

  return (
    <Paper
      component="aside"
      elevation={0}
      aria-label="Before you approve"
      sx={{
        position: "sticky",
        top: 16,
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: "background.paper",
      }}
    >
      {/* Pinned header: action, one status line, heading. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0 }}>
        <MUIButton
          variant={ButtonStyleType.Contained}
          onClick={onApprove}
          disabled={disabled}
          aria-describedby={RAIL_STATUS_ID}
          sx={{ width: "100%", height: 44, fontWeight: 600 }}
        >
          {isApproving ? "Approving..." : "Approve and load"}
        </MUIButton>
        <Typography
          id={RAIL_STATUS_ID}
          variant="body2"
          sx={{ display: "flex", alignItems: "center", gap: 0.75, color: tone, fontWeight: 600, lineHeight: 1.3 }}
        >
          {StatusIcon && <StatusIcon sx={{ fontSize: 15, flexShrink: 0 }} />}
          <span>{statusText}</span>
        </Typography>
        <Typography
          component="h2"
          variant="subtitle1"
          sx={{ fontWeight: 600, pt: 1.5, borderTop: 1, borderColor: "divider" }}
        >
          Before you approve
        </Typography>
      </Box>

      {/* Scrolling list. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, overflowY: "auto", minHeight: 0, pr: 0.5 }}>
        {scopeNote && (
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              alignItems: "flex-start",
              fontSize: 12,
              color: "text.secondary",
              bgcolor: "action.hover",
              borderRadius: 2,
              px: 1.25,
              py: 1,
            }}
          >
            <NoteIcon sx={{ fontSize: 14, mt: "1px", flexShrink: 0 }} />
            <Typography variant="caption">{scopeNote}</Typography>
          </Box>
        )}

        {checks.cards.map((card) => (
          <CheckCard key={card.key} card={card} onAcknowledge={onAcknowledge} />
        ))}

        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
          {checks.passLines.map((line) => (
            <PassLine key={line.key} line={line} />
          ))}
        </Box>

        {showFix && (
          <>
            <Divider />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                Fix the extraction
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                {onReviewMapping && (
                  <Tooltip title={fixDisabledReason ?? ""} disableHoverListener={!fixDisabledReason}>
                    <span>
                      <MUIButton
                        size={UIComponentSize.Small}
                        variant={ButtonStyleType.Outlined}
                        onClick={onReviewMapping}
                        disabled={!!fixDisabledReason}
                        sx={neutralButtonSx}
                      >
                        Field mapping
                      </MUIButton>
                    </span>
                  </Tooltip>
                )}
                {onReprocess && (
                  <Tooltip title={fixDisabledReason ?? ""} disableHoverListener={!fixDisabledReason}>
                    <span>
                      <MUIButton
                        size={UIComponentSize.Small}
                        variant={ButtonStyleType.Outlined}
                        onClick={onReprocess}
                        disabled={!!fixDisabledReason || isReprocessing}
                        sx={neutralButtonSx}
                      >
                        {isReprocessing ? "Reprocessing…" : "Reprocess"}
                      </MUIButton>
                    </span>
                  </Tooltip>
                )}
                <HintIcon
                  size="xs"
                  hint="Reprocess keeps your field mappings — change them first if values are wrong."
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

const CheckCard = ({
  card,
  onAcknowledge,
}: {
  card: ReviewCheckCard;
  onAcknowledge: (check: string, detail: string) => void;
}) => {
  const blocking = card.severity === "blocking";
  const Icon = blocking ? BlockingIcon : AdvisoryIcon;
  const color = toneText(card.severity);
  return (
    <Box
      role="group"
      aria-label={card.title}
      data-severity={card.severity}
      sx={{
        border: 1,
        borderColor: toneBorder(card.severity),
        bgcolor: toneFill(card.severity),
        borderRadius: 2.5,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Icon sx={{ fontSize: 15, color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color, flex: 1, lineHeight: 1.3 }}>
          {card.title}
        </Typography>
        {card.hint && <HintIcon size="xs" hint={card.hint} />}
      </Box>
      {card.body && (
        <Typography variant="caption" sx={{ color: "text.primary", lineHeight: 1.4 }}>
          {card.body}
        </Typography>
      )}
      {card.rows && card.rows.length > 0 && (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {card.rows.map((row) => (
            // Plain rows, no boxes (walkthrough feedback 2026-10-15), with the
            // count and its Show / Exclude on ONE line so the actions read as
            // belonging to that reason (walkthrough feedback 2026-10-26). The
            // row wraps only when a long label leaves no room, and then the
            // actions drop to a second line, still right-aligned. A hairline
            // separates reasons.
            <Box
              component="li"
              key={row.key}
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1,
                rowGap: 0.25,
                py: 0.5,
                "& + &": { borderTop: 1, borderColor: "divider" },
              }}
            >
              <Typography variant="caption" sx={{ lineHeight: 1.35, flex: "1 1 auto", minWidth: 0 }}>
                <strong>{row.count.toLocaleString()}</strong> · {row.label}
                {row.detail ? ` · ${row.detail}` : ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, ml: "auto", flexShrink: 0 }}>
                {row.onShow && (
                  <MUIButton
                    size={UIComponentSize.Small}
                    variant={ButtonStyleType.Text}
                    onClick={row.onShow}
                    aria-label={`Show ${row.label} rows in grid`}
                    sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  >
                    Show in grid
                  </MUIButton>
                )}
                {row.onExclude && (
                  <MUIButton
                    size={UIComponentSize.Small}
                    variant={ButtonStyleType.Outlined}
                    onClick={row.onExclude}
                    aria-label={`Exclude ${(row.excludeCount ?? row.count).toLocaleString()} ${row.label} rows`}
                    sx={{ ...neutralButtonSx, minWidth: 0, px: 1, fontSize: 12 }}
                  >
                    Exclude {(row.excludeCount ?? row.count).toLocaleString()}
                  </MUIButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {(card.actions.length > 0 || card.acknowledge) && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {card.actions.map((action) =>
            action.href ? (
              // A route, not a handler: a real link so it opens in a new tab (#7380).
              <MUIButton
                key={action.label}
                component={RouterLink}
                to={action.href}
                size={UIComponentSize.Small}
                variant={action.variant === "text" ? ButtonStyleType.Text : ButtonStyleType.Outlined}
                aria-label={action.ariaLabel}
                sx={action.variant === "text" ? undefined : neutralButtonSx}
              >
                {action.label}
              </MUIButton>
            ) : (
              <MUIButton
                key={action.label}
                size={UIComponentSize.Small}
                variant={action.variant === "text" ? ButtonStyleType.Text : ButtonStyleType.Outlined}
                onClick={action.onClick}
                aria-label={action.ariaLabel}
                sx={action.variant === "text" ? undefined : neutralButtonSx}
              >
                {action.label}
              </MUIButton>
            )
          )}
          {card.acknowledge && (
            <MUIButton
              size={UIComponentSize.Small}
              variant={ButtonStyleType.Text}
              onClick={() => onAcknowledge(card.key, card.acknowledge!.detail)}
              sx={{ fontWeight: 600 }}
            >
              {card.acknowledge.label}
            </MUIButton>
          )}
        </Box>
      )}
    </Box>
  );
};

const PassLine = ({ line }: { line: ReviewPassLine }) => {
  const Icon = line.state === "pass" ? PassIcon : line.state === "pending" ? PendingIcon : NoteIcon;
  const color = line.state === "pass" ? "success.main" : "text.disabled";
  return (
    <Box component="li" sx={{ display: "flex", alignItems: "flex-start", gap: 1, minHeight: 28, py: 0.5 }}>
      {/* "plain" lines (the counts) carry no icon: an icon beside a fact reads as a verdict. */}
      {line.state === "plain" ? (
        <Box sx={{ width: 15, flexShrink: 0 }} />
      ) : (
        <Icon sx={{ fontSize: 15, color, mt: "2px", flexShrink: 0 }} />
      )}
      <Typography variant="body2" sx={{ flex: 1, color: "text.secondary", lineHeight: 1.35 }}>
        {line.text}
      </Typography>
      {line.hint && <HintIcon size="xs" hint={line.hint} />}
      {line.action && (
        <MUIButton
          size={UIComponentSize.Small}
          variant={ButtonStyleType.Text}
          onClick={line.action.onClick}
          aria-label={line.action.ariaLabel}
          sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 12, fontWeight: 600, alignSelf: "flex-start" }}
        >
          {line.action.label}
        </MUIButton>
      )}
    </Box>
  );
};
